import React from 'react';
import config from './config';
import { POSITIONING, RECORD_KEEPING } from './compliance/mtdFacts';


const HelpPage = () => {
    return (
        <div className="max-w-4xl mx-auto px-6 pb-24 text-slate-300">
            <header className="mb-12 text-center">
                <div className="inline-block px-4 py-1.5 mb-6 text-sm font-semibold tracking-wide text-indigo-400 uppercase bg-indigo-500/10 rounded-full border border-indigo-500/20">
                    User Guide
                </div>
                <h1 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-4">
                    Using Your Expense Assistant
                </h1>
                <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                    A comprehensive guide to capturing receipts, logging mileage, and generating reports.
                </p>
            </header>

            <div className="space-y-16">
                {/* Section 1: Capturing Receipts */}
                <section>
                    <div className="flex items-center gap-3 mb-6">
                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-500/20 text-indigo-400 font-bold border border-indigo-500/30">1</span>
                        <h2 className="text-2xl font-bold text-white">Capturing Receipts & Invoices</h2>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        {/* WhatsApp Card */}
                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-lg">
                            <div className="flex items-center gap-2 mb-4 text-green-400 font-bold">
                                <span className="text-xl">📸</span>
                                <h3>Receipt Photos</h3>
                            </div>
                            <p className="text-sm mb-4">
                                Simply snap a photo of your receipt(s) and send it to our WhatsApp number.
                            </p>
                            <ul className="space-y-3 text-sm text-slate-400">
                                <li className="flex gap-2">
                                    <span className="text-indigo-400">•</span>
                                    <span><strong>No typing required:</strong> Our AI automatically extracts Merchant, Date, Total and VAT for UK receipts.</span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-indigo-400">•</span>
                                    <span><strong>Review Process:</strong> If any fields are unclear, you'll receive a link to review. Simple! Just follow the photo tips below and there shouldn't be much to review.</span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-indigo-400">•</span>
                                    <span><strong>Batch Scanning:</strong> Combine up to 6 receipts in one photo (ensure no overlap).</span>
                                </li>
                            </ul>
                            <div className="mt-4 p-3 bg-slate-800/50 rounded-lg text-xs md:text-sm border border-slate-700">
                                <span className="text-slate-200 font-semibold">Photo Tips:</span> Make sure it's well-lit and the text is clear. If the receipt is crumpled, try to smooth it out flat before snapping. Especially for batch photos!
                            </div>
                            <div className="mt-6">
                                <a href="/speed-test" className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600/20 text-indigo-400 border border-indigo-500/30 rounded-xl font-bold text-sm hover:bg-indigo-600/40 transition-all shadow-lg shadow-indigo-500/10">
                                    <span>⏱️</span> Try the 5-second Speed Test
                                </a>
                            </div>
                        </div>

                        {/* Email Card */}
                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-lg">
                            <div className="flex items-center gap-2 mb-4 text-indigo-400 font-bold">
                                <span className="text-xl">📧</span>
                                <h3>Email Forwarding</h3>
                            </div>
                            <p className="text-sm mb-4">
                                Received an invoice by email? Forward it to your unique ingestion email.
                            </p>
                            <div className="flex items-center justify-evenly py-5 px-3 mb-6 rounded-xl bg-black/40 border border-white/5 shadow-inner">
                                <div className="flex flex-col items-center justify-center">
                                    <span className="text-[30px] leading-none">📄</span>
                                    <span className="text-[11px] text-slate-500 leading-tight text-center font-mono mt-2">email<br />invoice</span>
                                </div>
                                <div className="text-indigo-500 animate-pulse">
                                    <svg className="w-7 h-7 transform scale-y-[1.15]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                </div>
                                <div className="bg-indigo-500/10 border border-indigo-500/30 px-4 py-2.5 rounded-lg text-sm font-mono flex flex-col items-start gap-1 min-w-0 ml-1.5">
                                    <span className="text-[10px] text-indigo-400/70 font-sans font-semibold uppercase tracking-widest">Example:</span>
                                    <span className="text-indigo-300 break-all">a7x9f2k8m4@zapexpenses.com</span>
                                </div>
                            </div>
                            <div className="space-y-4 text-sm text-slate-400">
                                <div>
                                    <strong className="text-white block mb-1">Supported Formats:</strong>
                                    <ul className="list-disc pl-5 space-y-1">
                                        <li>PDF Attachments (multi-page up to 5 pages)</li>
                                        <li>HTML Body (e.g., Amazon order confirmations)</li>
                                        <li>Images (JPG/PNG attachments)</li>
                                    </ul>
                                </div>
                                <div className="text-[13px] text-amber-500/80 bg-amber-500/10 p-2 rounded border border-amber-500/20">
                                    Ensure the email is sent from your registered email address so we know it's you.
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Section 2: Logging Mileage */}
                <section>
                    <div className="flex items-center gap-3 mb-6">
                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-500/20 text-indigo-400 font-bold border border-indigo-500/30">2</span>
                        <h2 className="text-2xl font-bold text-white">Logging Mileage</h2>
                    </div>

                    <div className="bg-gradient-to-br from-slate-900 to-slate-900/50 border border-slate-800 rounded-2xl p-8">
                        <p className="text-slate-400 mb-6">
                            Log business trips in plain English via WhatsApp. We calculate the reimbursement value automatically based on standard HMRC rates.
                        </p>

                        <div className="grid md:grid-cols-2 gap-8">
                            <div>
                                <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                                    <span>🚗</span> How to Log a Trip
                                </h3>
                                <p className="text-sm text-slate-400 mb-2">
                                    Send a message describing your journey. HMRC requires:
                                </p>
                                <ul className="list-disc pl-8 space-y-1 text-sm text-slate-400 mb-4">
                                    <li>To/From locations</li>
                                    <li>Purpose</li>
                                    <li>Distance <span className="text-slate-500">(calculated automatically if you leave it out - make sure you state the full postcodes)</span></li>
                                    <li>Date of travel <span className="text-slate-500">(defaults to today's date if missing)</span></li>
                                    <li>Vehicle <span className="text-slate-500">(defaults to Personal Car if missing)</span></li>
                                </ul>
                                <div className="space-y-2">
                                    <div className="bg-green-500/10 border border-green-500/20 p-3 rounded-lg text-green-200 text-sm font-mono">
                                        "Drove from LE5 3AD to N18 2FP for client meeting"
                                    </div>
                                    <div className="bg-green-500/10 border border-green-500/20 p-3 rounded-lg text-green-200 text-sm font-mono">
                                        "Trip to Manchester from Leicester for conference, 85 miles in Ford Focus"
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h3 className="text-white font-bold mb-3">Important Notes</h3>
                                <ul className="space-y-3 text-sm text-slate-400">
                                    <li className="flex gap-2">
                                        <span className="text-indigo-400">ℹ️</span>
                                        <span>
                                            <strong>Vehicle Defaults:</strong> Defaults to "Personal Car" if unspecified. Uses HMRC rates (45p first 10k miles, 25p thereafter).
                                        </span>
                                    </li>
                                    <li className="flex gap-2">
                                        <span className="text-indigo-400">🔄</span>
                                        <span>
                                            <strong>Round Trips:</strong> Log total distance for return journeys (e.g., "Round trip from Leicester to London, 239 miles").
                                        </span>
                                    </li>
                                    <li className="flex gap-2">
                                        <span className="text-indigo-400">⚠️</span>
                                        <span>
                                            <strong>Missing Details:</strong> If you forget a purpose or location, we'll flag it for review.
                                        </span>
                                    </li>
                                </ul>

                                {/* User Feedback Bubble */}
                                <div className="!mt-8 flex justify-start">
                                    <div className="relative group w-full max-w-sm">
                                        <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-emerald-500 rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>
                                        <div className="relative bg-[#020617] border border-indigo-500/20 rounded-xl p-4 shadow-2xl backdrop-blur-sm">
                                            <div className="flex items-start gap-3">
                                                <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center text-xl shrink-0 group-hover:rotate-6 transition-transform">
                                                    🎙️
                                                </div>
                                                <div>
                                                    <p className="text-white font-bold text-[15px] leading-tight mb-1">Voice-Only Mileage?</p>
                                                    <p className="text-slate-400 text-xs leading-relaxed">
                                                        Would this be worthwhile?<br/>
                                                        <a href={`mailto:${config.SUPPORT_EMAIL}`} className="text-indigo-400 font-bold hover:text-indigo-300 transition-colors inline-flex items-center gap-1 mt-1">
                                                            Let us know <span className="text-[10px]">→</span>
                                                        </a>
                                                    </p>
                                                </div>
                                            </div>
                                            {/* Speech bubble tail pointing left on desktop (towards green boxes) */}
                                            <div className="hidden md:block absolute top-1/2 -left-2 -translate-y-1/2 w-4 h-4 bg-[#020617] border-l border-b border-indigo-500/20 transform rotate-45 group-hover:border-indigo-500/30 transition-colors"></div>
                                            {/* Speech bubble tail pointing up on mobile */}
                                            <div className="md:hidden absolute -top-2 left-6 w-4 h-4 bg-[#020617] border-t border-l border-indigo-500/20 transform rotate-45 group-hover:border-indigo-500/30 transition-colors"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Section 3: Reports & Reviews */}
                <section>
                    <div className="flex items-center gap-3 mb-6">
                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-500/20 text-indigo-400 font-bold border border-indigo-500/30">3</span>
                        <h2 className="text-2xl font-bold text-white">Reports & Reviews</h2>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        <div>
                            <h3 className="text-lg font-bold text-white mb-4">Requesting a Report</h3>
                            <p className="text-sm text-slate-400 mb-4">Ask for your expenses anytime via WhatsApp or email:</p>
                            <ul className="space-y-2 mb-6 text-sm font-mono text-indigo-300">
                                <li className="bg-indigo-500/10 inline-block px-2 py-1 rounded">"Send me last month's report"</li>
                                <li className="bg-indigo-500/10 inline-block px-2 py-1 rounded">"Export expenses for December 2025"</li>
                            </ul>
                            <div className="text-sm text-slate-400">
                                <p className="mb-2 font-semibold text-slate-300">You'll receive a ZIP package containing:</p>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li><strong>Expenses CSV:</strong> detailed breakdown of receipts.</li>
                                    <li><strong>Mileage CSV:</strong> separate log of business trips.</li>
                                    <li><strong>Receipt Images:</strong> all receipts & invoices from our archive.</li>
                                </ul>
                                <div className="mt-6 p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-xl">
                                    <p className="text-indigo-200 text-sm font-semibold mb-1">Accountant CC</p>
                                    <p className="text-indigo-100/70 text-sm">
                                        You can add your accountant's email in your dashboard to automatically CC them on these monthly exports.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                                The "Needs Review" System ⚠️
                            </h3>
                            <p className="text-sm text-slate-400 mb-4">
                                Our AI flags items that look uncertain (less than 95% confidence) or are missing critical info.
                            </p>
                            <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl mb-4">
                                <p className="text-amber-200 text-sm font-semibold mb-2">Check Pending Items</p>
                                <p className="text-amber-100/70 text-sm">
                                    Text "What needs review?" to see a list of items requiring attention.
                                </p>
                            </div>

                        </div>
                    </div>
                </section>

                {/* Section 4: Security & Data Protection */}
                <section>
                    <div className="flex items-center gap-3 mb-6">
                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-400 font-bold border border-emerald-500/30">4</span>
                        <h2 className="text-2xl font-bold text-white">Security & Data Protection</h2>
                    </div>

                    <p className="text-slate-400 mb-6">
                        Your financial data deserves serious protection. Here's exactly how we keep it safe.
                    </p>

                    <div className="grid md:grid-cols-2 gap-8 items-stretch">
                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 text-center shadow-xl flex flex-col items-center justify-center">
                            <div className="w-12 h-12 mx-auto mb-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-2xl">🔒</div>
                            <h2 className="text-xl font-bold mb-3 text-white">Enterprise-Grade Security</h2>
                            <p className="text-slate-400 text-base mb-0">
                                Your data is secured with AES-256 encryption. We employ strict access controls to ensure your financial records remain private and safe.
                            </p>
                        </div>

                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 text-center shadow-xl flex flex-col items-center justify-center">
                            <div className="w-12 h-12 mx-auto mb-4 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-2xl">📋</div>
                            <h2 className="text-xl font-bold mb-3 text-white">Data Retention</h2>
                            <p className="text-slate-400 text-base mb-4">
                                {POSITIONING.retention}
                            </p>
                            <div className="bg-emerald-500/10 border border-emerald-500/30 p-4 rounded-xl text-left text-sm text-slate-300 w-full mb-0">
                                <strong className="text-emerald-400 block mb-1">HMRC Requirement:</strong>
                                "HMRC requires you to keep business records for {RECORD_KEEPING.duration}. {RECORD_KEEPING.lateReturnsNote}"
                                <div className="text-[10px] text-slate-500 italic mt-2 border-t border-emerald-500/20 pt-2">
                                    Source: <a href={RECORD_KEEPING.source} target="_blank" rel="noopener noreferrer" className="underline hover:text-emerald-400">{RECORD_KEEPING.sourceAlias}</a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                        <p className="text-emerald-200 text-sm">
                            <strong>In summary:</strong> Your data is encrypted, access is locked down, and you can export or request deletion at any time. {POSITIONING.noSubmit}<br />
                            For full details, see our <a href="/privacy.html" className="text-emerald-300 underline hover:text-emerald-200">Privacy Policy</a>.
                        </p>
                    </div>
                </section>

                {/* Footer / Pro Tips */}
                <footer className="mt-16 pt-8 border-t border-slate-800">
                    <div className="bg-gradient-to-r from-indigo-900/40 to-slate-900 border border-indigo-500/20 rounded-2xl p-6 md:p-8">
                        <h3 className="text-xl font-bold text-white mb-6 flex items-center justify-center md:justify-start gap-2">
                            💡 Pro Tips & Good to Know
                        </h3>
                        <div className="grid md:grid-cols-3 gap-10 text-sm text-slate-400 text-center md:text-left">
                            <div>
                                <strong className="text-white block mb-1">"Help" Command</strong>
                                Text "Help" at any time to see a cheat sheet of commands.
                            </div>
                            <div>
                                <strong className="text-white block mb-1">Fair Usage</strong>
                                Standard 300 expenses/month limit for AI processing (10/day). Please contact us if you expect to exceed this.
                            </div>
                            <div>
                                <strong className="text-white block mb-1">Support & Feedback</strong>
                                We are a small, agile team - expect fast responses to feedback and real human support when you need it at <a href={`mailto:${config.SUPPORT_EMAIL}`} className="text-indigo-400 underline hover:text-indigo-300">{config.SUPPORT_EMAIL}</a>.
                            </div>
                        </div>
                        <div className="mt-6 pt-6 border-t border-indigo-500/10 text-xs text-slate-300 text-center">
                            <strong>Disclaimer:</strong> While we automate data extraction and apply standard tax rules, you remain responsible for the accuracy of your expense reports and compliance with HMRC regulations. Always review your monthly reports before filing.
                        </div>
                    </div>
                </footer>

            </div>
        </div>
    );
};

export default HelpPage;
